import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Car, User, Clock, Edit, Truck, Star } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import OrderPopup from '@/components/OrderPopup';
import DriverMap from '@/components/DriverMap';
import { calculateOnTimeRate } from '@/utils/deliveryMetrics';
import { isRideDriverEmail } from '@/config/features';

interface Order {
  id: string;
  customer_id: string;
  restaurant_id: string;
  driver_id: string | null;
  status: string;
  delivery_address: string;
  delivery_type: string;
  created_at: string;
  customer_name: string;
  customer_phone: string;
  notes: string | null;
  restaurants: {
    name: string;
    address: string;
  };
  total_amount: number;
  delivery_fee: number;
}

interface DriverProfile {
  full_name: string;
  email: string;
  phone_number: string;
  license_number: string;
  vehicle_type: string;
}

interface DriverRatings {
  averageRating: number;
  totalRatings: number;
}

import { useDriverOrderNotifications } from '@/hooks/useDriverOrderNotifications';

const DriverDashboardPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [isOnline, setIsOnline] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [queuedOrders, setQueuedOrders] = useState<Order[]>([]);
  const [todayStats, setTodayStats] = useState({ deliveries: 0, onTimeRate: 95 });
  const [driverProfile, setDriverProfile] = useState<DriverProfile | null>(null);
  const [driverRatings, setDriverRatings] = useState<DriverRatings>({ averageRating: 0, totalRatings: 0 });
  const [driverLocation, setDriverLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [isOrderPopupMinimized, setIsOrderPopupMinimized] = useState(false);

  // Initialize driver push notifications
  useDriverOrderNotifications(user?.id || '');

  // Location tracking - accurate, filtered, and kept alive with wake lock
  useEffect(() => {
    if (!isOnline || !user || !navigator.geolocation) return;

    let watchId: number | null = null;
    let wakeLock: any = null;
    let lastPos: { lat: number; lng: number; t: number } | null = null;
    let lastWriteAt = 0;
    let fallbackTimer: number | null = null;

    const writeLocation = async (lat: number, lng: number) => {
      try {
        await supabase.from('driver_profiles').upsert({
          user_id: user.id,
          current_latitude: lat,
          current_longitude: lng,
          last_location_update: new Date().toISOString(),
        }, { onConflict: 'user_id' });
      } catch (err) {
        console.error('Error updating location:', err);
      }
    };

    const handlePosition = (position: GeolocationPosition) => {
      const { latitude, longitude, accuracy } = position.coords;

      // Reject very low-accuracy fixes (wifi/cell towers) — they cause the "teleport" jumps
      if (accuracy && accuracy > 100) {
        console.log('Skipping low-accuracy fix:', accuracy, 'm');
        return;
      }

      const now = Date.now();

      // Reject unrealistic jumps (>150 km/h between fixes)
      if (lastPos) {
        const dt = (now - lastPos.t) / 1000;
        const R = 6371000;
        const dLat = ((latitude - lastPos.lat) * Math.PI) / 180;
        const dLng = ((longitude - lastPos.lng) * Math.PI) / 180;
        const a =
          Math.sin(dLat / 2) ** 2 +
          Math.cos((lastPos.lat * Math.PI) / 180) *
            Math.cos((latitude * Math.PI) / 180) *
            Math.sin(dLng / 2) ** 2;
        const dist = 2 * R * Math.asin(Math.sqrt(a));
        if (dt > 0 && dist / dt > 42) {
          console.log('Skipping teleport fix:', dist, 'm in', dt, 's');
          return;
        }
      }

      lastPos = { lat: latitude, lng: longitude, t: now };
      setDriverLocation({ lat: latitude, lng: longitude });

      // Throttle DB writes to every 4s minimum
      if (now - lastWriteAt >= 4000) {
        lastWriteAt = now;
        writeLocation(latitude, longitude);
      }
    };

    const options: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0,
    };

    watchId = navigator.geolocation.watchPosition(
      handlePosition,
      (error) => console.error('Location error:', error),
      options,
    );

    // Fallback: if browser suspends watchPosition, poll every 15s to refresh last_location_update
    fallbackTimer = window.setInterval(() => {
      navigator.geolocation.getCurrentPosition(handlePosition, () => {}, options);
    }, 15000);

    // Keep screen awake so the browser doesn't pause geolocation
    (async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await (navigator as any).wakeLock.request('screen');
          wakeLock.addEventListener?.('release', () => {
            console.log('Wake lock released');
          });
        }
      } catch (err) {
        console.log('Wake lock unavailable:', err);
      }
    })();

    const handleVisibility = async () => {
      if (document.visibilityState === 'visible') {
        try {
          if ('wakeLock' in navigator && !wakeLock) {
            wakeLock = await (navigator as any).wakeLock.request('screen');
          }
        } catch { /* ignore */ }
        // Nudge a fresh position on resume
        navigator.geolocation.getCurrentPosition(handlePosition, () => {}, options);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      if (fallbackTimer) clearInterval(fallbackTimer);
      document.removeEventListener('visibilitychange', handleVisibility);
      if (wakeLock) {
        try { wakeLock.release(); } catch { /* ignore */ }
        wakeLock = null;
      }
    };
  }, [isOnline, user]);

  // Check authentication and redirect ride drivers
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
    }
    if (!authLoading && user && isRideDriverEmail(user.email)) {
      navigate('/ride-driver-dashboard');
    }
  }, [user, authLoading, navigate]);

  // Load driver data and setup realtime
  useEffect(() => {
    if (user) {
      console.log('Driver dashboard loading for user:', user.id);
      loadDriverStatus();
      fetchDriverOrders();
      fetchTodayStats();
      fetchDriverProfile();
      fetchDriverRatings();
    }
  }, [user]);

  // Setup realtime listener for assigned orders
  useEffect(() => {
    if (!user) return;

    console.log('Setting up realtime listener for driver:', user.id);
    
    const channel = supabase
      .channel('driver-orders-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `driver_id=eq.${user.id}`
        },
        async (payload) => {
          console.log('Order change detected for driver:', payload);
          
          if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
            const order = payload.new;
            
            // Only handle delivery orders assigned to this driver
            if (order.delivery_type === 'pickup' || order.driver_id !== user.id) {
              console.log('Ignoring order not for this driver:', order.id);
              return;
            }

            console.log('Processing delivery order update:', order.status);
            
            // Refresh all driver orders to maintain proper queue
            fetchDriverOrders();
            
            // Show toast notifications for new assignments
            if (order.status === 'confirmed' && payload.eventType === 'INSERT') {
              toast({
                title: "New Order Assigned!",
                description: `Delivery order queued from restaurant`,
                duration: 5000,
              });
            } else if (order.status === 'ready_for_pickup') {
              toast({
                title: "Order Ready!",
                description: "Your order is ready for pickup at the restaurant.",
                duration: 4000,
              });
            }
          }
        }
      )
      .subscribe((status) => {
        console.log('Realtime subscription status:', status);
      });

    return () => {
      console.log('Cleaning up realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Load driver online status
  const loadDriverStatus = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('driver_profiles')
        .select('is_active')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading driver status:', error);
        return;
      }

      if (data) {
        setIsOnline(data.is_active || false);
        console.log('Driver status loaded:', data.is_active);
      }
    } catch (error) {
      console.error('Error in loadDriverStatus:', error);
    }
  };

  // Get all driver orders and organize them properly
  const fetchDriverOrders = async () => {
    if (!user) return;

    try {
      console.log('Fetching driver orders for:', user.id);
      
      // Get all orders assigned to this driver that are in progress
      const { data: driverOrders, error } = await supabase
        .from('orders')
        .select(`
          *,
          restaurants (
            name,
            address
          )
        `)
        .eq('driver_id', user.id)
        .eq('delivery_type', 'delivery')
        .in('status', ['confirmed', 'preparing', 'ready_for_pickup', 'picked_up'])
        .order('created_at', { ascending: true }); // Oldest first for proper queue

      if (error) {
        console.error('Error fetching driver orders:', error);
        return;
      }

      if (driverOrders && driverOrders.length > 0) {
        console.log('Found driver orders:', driverOrders.length);
        
        // The current order is the first one that's either picked_up or the oldest one
        let activeOrder = driverOrders.find(order => order.status === 'picked_up');
        if (!activeOrder) {
          activeOrder = driverOrders[0]; // Get the oldest order if none are picked up
        }
        
        // Only update current order if it's different to prevent unnecessary re-renders
        if (!currentOrder || currentOrder.id !== activeOrder.id) {
          setCurrentOrder(activeOrder);
          console.log('Active order set to:', activeOrder.id, 'Status:', activeOrder.status);
        }
        
        // Remaining orders are queued (excluding the current active order)
        const queued = driverOrders.filter(order => order.id !== activeOrder.id);
        setQueuedOrders(queued);
        
        console.log('Queued orders:', queued.length);
      } else {
        setCurrentOrder(null);
        setQueuedOrders([]);
        console.log('No active delivery orders found');
      }
    } catch (error) {
      console.error('Error in fetchDriverOrders:', error);
    }
  };

  // Updated function to use the delivery metrics utility
  const fetchTodayStats = async () => {
    if (!user) return;

    try {
      const metrics = await calculateOnTimeRate(user.id);
      setTodayStats({
        deliveries: metrics.totalDeliveries,
        onTimeRate: metrics.onTimeRate
      });
    } catch (error) {
      console.error('Error in fetchTodayStats:', error);
      // Fallback to simple count if metrics calculation fails
      const today = new Date().toISOString().split('T')[0];
      
      const { data } = await supabase
        .from('orders')
        .select('*')
        .eq('driver_id', user.id)
        .eq('status', 'delivered')
        .eq('delivery_type', 'delivery')
        .gte('created_at', today + 'T00:00:00.000Z')
        .lt('created_at', today + 'T23:59:59.999Z');

      const deliveries = data?.length || 0;
      setTodayStats({ deliveries, onTimeRate: 95 }); // Fallback rate
    }
  };

  // Get driver profile
  const fetchDriverProfile = async () => {
    if (!user) return;

    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('full_name, email, phone')
        .eq('id', user.id)
        .single();

      const { data: applicationData } = await supabase
        .from('driver_applications')
        .select('phone_number, license_number, vehicle_type')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileData) {
        setDriverProfile({
          full_name: profileData.full_name || '',
          email: profileData.email || user.email || '',
          phone_number: applicationData?.phone_number || profileData.phone || '',
          license_number: applicationData?.license_number || '',
          vehicle_type: applicationData?.vehicle_type || ''
        });
      }
    } catch (error) {
      console.error('Error fetching driver profile:', error);
    }
  };

  // Fetch driver ratings
  const fetchDriverRatings = async () => {
    if (!user) return;

    try {
      const { data: ratings, error } = await supabase
        .from('ratings')
        .select('driver_rating')
        .eq('driver_id', user.id)
        .not('driver_rating', 'is', null);

      if (error) {
        console.error('Error fetching driver ratings:', error);
        return;
      }

      if (ratings && ratings.length > 0) {
        const totalRatings = ratings.length;
        const sum = ratings.reduce((acc, rating) => acc + (rating.driver_rating || 0), 0);
        const averageRating = Math.round((sum / totalRatings) * 10) / 10; // Round to 1 decimal place
        
        setDriverRatings({
          averageRating,
          totalRatings
        });
      } else {
        setDriverRatings({ averageRating: 0, totalRatings: 0 });
      }
    } catch (error) {
      console.error('Error in fetchDriverRatings:', error);
    }
  };

  // Updated toggle online status function with automatic assignment
  const toggleOnlineStatus = async () => {
    if (!user) return;
    
    const newStatus = !isOnline;
    console.log('Toggling online status to:', newStatus);

    try {
      const { error } = await supabase
        .from('driver_profiles')
        .upsert({
          user_id: user.id,
          is_active: newStatus
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('Error updating status:', error);
        return;
      }

      setIsOnline(newStatus);

      if (newStatus) {
        // When going online, check for pending orders and assign them
        try {
          const { data: assignedCount, error: assignError } = await supabase
            .rpc('assign_pending_orders_to_driver', {
              driver_user_id: user.id
            });

          if (assignError) {
            console.error('Error assigning pending orders:', assignError);
          } else if (assignedCount && assignedCount > 0) {
            toast({
              title: "Orders Assigned!",
              description: `You've been assigned ${assignedCount} pending order${assignedCount > 1 ? 's' : ''}`,
              duration: 5000,
            });
          }
        } catch (error) {
          console.error('Error calling assign_pending_orders_to_driver:', error);
        }

        toast({
          title: "You're now online",
          description: "You'll automatically receive orders from nearby restaurants",
          duration: 4000,
        });
        
        // Check for orders when going online (including newly assigned ones)
        setTimeout(() => {
          fetchDriverOrders();
        }, 1000);
      } else {
        toast({
          title: "You're now offline",
          description: "You won't receive new orders",
          duration: 4000,
        });
        setCurrentOrder(null);
        setQueuedOrders([]);
      }
    } catch (error) {
      console.error('Error in toggleOnlineStatus:', error);
    }
  };

  const handlePickup = async () => {
    if (!currentOrder || !user) {
      console.error('No current order or user for pickup');
      return;
    }

    try {
      const newStatus = 'picked_up';
      const toastTitle = "Order Picked Up!";
      const toastDescription = "Now navigate to customer location";

      if (currentOrder.status !== 'confirmed' && currentOrder.status !== 'ready_for_pickup' && currentOrder.status !== 'preparing') {
        console.error('Invalid status for pickup:', currentOrder.status);
        toast({
          title: "Cannot Update",
          description: `Order is in '${currentOrder.status}' status and cannot be picked up yet.`,
          duration: 4000,
        });
        return;
      }
      
      console.log(`Updating order from ${currentOrder.status} to ${newStatus}:`, currentOrder.id);
      
      const { error } = await supabase
        .from('orders')
        .update({ status: newStatus })
        .eq('id', currentOrder.id)
        .eq('driver_id', user.id);

      if (error) {
        console.error('Error updating order:', error);
        toast({
          title: "Error",
          description: `Failed to update order status: ${error.message}`,
          duration: 5000,
        });
        return;
      }

      console.log('Order picked up successfully');
      
      toast({
        title: toastTitle,
        description: toastDescription,
        duration: 4000,
      });
      
      // Refresh orders to update the queue
      fetchDriverOrders();
      
    } catch (error) {
      console.error('Error in handlePickup:', error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        duration: 4000,
      });
    }
  };

  const handleDropOff = async () => {
    if (!currentOrder || !user) {
      console.error('No current order or user for drop off');
      return;
    }

    try {
      console.log('Updating order to delivered:', currentOrder.id);
      
      const { error } = await supabase
        .from('orders')
        .update({ 
          status: 'delivered',
          completed_at: new Date().toISOString()
        })
        .eq('id', currentOrder.id)
        .eq('driver_id', user.id);

      if (error) {
        console.error('Error updating order:', error);
        toast({
          title: "Error",
          description: `Failed to complete delivery: ${error.message}`,
          duration: 5000,
        });
        return;
      }

      console.log('Order delivered successfully');

      toast({
        title: "Order Delivered!",
        description: "Great job! Moving to next order.",
        duration: 4000,
      });

      // Check for pending orders to assign to this now-available driver
      try {
        const { data: assignedCount, error: assignError } = await supabase
          .rpc('assign_pending_orders_to_driver', {
            driver_user_id: user.id
          });

        if (assignError) {
          console.error('Error assigning pending orders after delivery:', assignError);
        } else if (assignedCount && assignedCount > 0) {
          toast({
            title: "New Orders Assigned!",
            description: `You've been assigned ${assignedCount} new order${assignedCount > 1 ? 's' : ''}`,
            duration: 5000,
          });
        }
      } catch (error) {
        console.error('Error calling assign_pending_orders_to_driver after delivery:', error);
      }

      // Refresh orders - this will automatically move to the next order in queue
      fetchDriverOrders();
      
      // Refresh stats
      setTimeout(() => {
        fetchTodayStats();
      }, 1000);
      
    } catch (error) {
      console.error('Error in handleDropOff:', error);
    }
  };

  const handleNavigate = (destination: 'pickup' | 'dropoff') => {
    if (!currentOrder) return;
    
    const address = destination === 'pickup' 
      ? currentOrder.restaurants?.address 
      : currentOrder.delivery_address;
      
    if (address) {
      const encodedAddress = encodeURIComponent(address);
      const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
      window.open(googleMapsUrl, '_blank');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!user) return null;

  // Format order for popup - only show total amount, not breakdown
  const orderForPopup = currentOrder ? {
    id: currentOrder.id,
    customerName: currentOrder.customer_name || 'Customer',
    customerPhone: currentOrder.customer_phone,
    address: currentOrder.delivery_address,
    estimatedTime: '15-20 min',
    distance: '2.5 km',
    status: currentOrder.status as 'pending' | 'confirmed' | 'ready' | 'ready_for_pickup' | 'picked_up' | 'delivered',
    pickupAddress: currentOrder.restaurants?.name + ' - ' + currentOrder.restaurants?.address,
    totalAmount: currentOrder.total_amount,
    notes: currentOrder.notes
  } : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/home')}>
              <ArrowLeft className="h-6 w-6" />
            </Button>
            <h1 className="text-xl font-semibold">Driver Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${isOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
            <Button
              onClick={toggleOnlineStatus}
              className={`${
                isOnline 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-green-600 hover:bg-green-700'
              }`}
              size="sm"
            >
              <Car className="h-4 w-4 mr-2" />
              {isOnline ? 'Go Offline' : 'Go Online'}
            </Button>
          </div>
        </div>
      </div>

      <div className="p-4">
        <Tabs defaultValue="drive" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="drive">
              <Car className="h-4 w-4 mr-2" />
              Drive
            </TabsTrigger>
            <TabsTrigger value="profile">
              <User className="h-4 w-4 mr-2" />
              Profile
            </TabsTrigger>
          </TabsList>

          <TabsContent value="drive" className="space-y-4 mt-6">
            {isOnline ? (
              <div className="space-y-4">
                {/* Map Container */}
                <Card>
                  <CardContent className="p-0">
                    <div className="h-96 w-full">
                      <DriverMap 
                        currentOrder={currentOrder}
                        queuedOrders={queuedOrders}
                        driverLocation={driverLocation}
                        onNavigate={handleNavigate}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Order Status Card */}
                {currentOrder ? (
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                          <Truck className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className="font-semibold">Active Order #{currentOrder.id.slice(0, 8)}</h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              currentOrder.status === 'picked_up' 
                                ? 'bg-blue-100 text-blue-800' 
                                : currentOrder.status === 'ready_for_pickup'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {currentOrder.status.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">Customer: {currentOrder.customer_name}</p>
                          <p className="text-sm text-gray-600">Restaurant: {currentOrder.restaurants?.name}</p>
                        </div>
                      </div>
                      
                      {queuedOrders.length > 0 && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm font-medium text-blue-800">
                            {queuedOrders.length} order{queuedOrders.length > 1 ? 's' : ''} in queue
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="p-6 text-center">
                      <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <h3 className="text-lg font-semibold mb-2">Waiting for Orders</h3>
                      <p className="text-gray-600 text-sm">You're online and ready to receive orders</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 rounded-full bg-gray-100 mx-auto mb-4 flex items-center justify-center">
                    <Car className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">You're Offline</h3>
                  <p className="text-gray-600 text-sm">Go online to start receiving orders from restaurants</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="profile" className="space-y-4 mt-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4">Today's Performance</h3>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600 mb-1">
                      {todayStats.deliveries}
                    </div>
                    <p className="text-sm text-gray-600">Deliveries</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600 mb-1">
                      {todayStats.onTimeRate}%
                    </div>
                    <p className="text-sm text-gray-600">On-Time Rate</p>
                  </div>

                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Star className="h-5 w-5 text-yellow-500 fill-current" />
                      <span className="text-2xl font-bold text-yellow-600">
                        {driverRatings.averageRating || 'N/A'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Rating ({driverRatings.totalRatings} reviews)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {driverProfile && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Driver Information</h3>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate('/edit-profile')}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600">Full Name</p>
                      <p className="font-medium">{driverProfile.full_name || 'Not set'}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-medium">{driverProfile.email}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600">Phone Number</p>
                      <p className="font-medium">{driverProfile.phone_number || 'Not set'}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600">License Number</p>
                      <p className="font-medium">{driverProfile.license_number || 'Not set'}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600">Vehicle Type</p>
                      <p className="font-medium">{driverProfile.vehicle_type || 'Not set'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <OrderPopup
        isVisible={!!currentOrder}
        isMinimized={isOrderPopupMinimized}
        onMinimize={() => setIsOrderPopupMinimized(true)}
        onMaximize={() => setIsOrderPopupMinimized(false)}
        onPickup={handlePickup}
        onDropOff={handleDropOff}
        onNavigate={handleNavigate}
        order={orderForPopup}
      />
    </div>
  );
};

export default DriverDashboardPage;
